/**
 * @typedef {Object} ChatbotConfig
 * @property {number} type - The type of chatbot (2: Crisp, 3: Hubspot, 4: chatbot.com, 5: landbot.io)
 * @property {string} customId - The custom ID for the chatbot
 */

/**
 * Service responsible for injecting third-party scripts into the document
 */
export class ScriptInjectionService {
  static monitorScriptLoad(scriptElement, scriptName) {
    const start = performance.now();
    
    scriptElement.addEventListener('load', () => {
      const duration = performance.now() - start;
      
      // Log performance in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`Script ${scriptName} loaded in ${Math.round(duration)}ms`);
      }
      
      // Send to analytics
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'script_load',
          script_name: scriptName,
          load_time_ms: Math.round(duration)
        });
      }
    });

    scriptElement.addEventListener('error', (error) => {
      console.error(`Error loading script ${scriptName}:`, error);
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'script_error',
          script_name: scriptName,
          error_message: error.message
        });
      }
    });
  }

  /**
   * Creates and injects a script element into the document
   * @param {Object} options - Script injection options
   * @param {string} options.content - The script content
   * @param {string} [options.src] - The script source URL
   * @param {Object} [options.attributes] - Additional script attributes
   * @param {string} [options.name] - The name of the script
   * @returns {HTMLScriptElement} The injected script element
   */
  static injectScript({ content, src, attributes = {}, name = 'unknown' }) {
    try {
      const script = document.createElement('script');
      
      if (src) {
        script.src = src;
        script.async = true;
        this.monitorScriptLoad(script, name);
      } else if (content) {
        script.innerHTML = content;
      }

      // Apply additional attributes
      Object.entries(attributes).forEach(([key, value]) => {
        script[key] = value;
      });

      // Add accessibility attributes
      script.setAttribute('data-script-name', name);
      if (attributes.id) {
        script.setAttribute('aria-describedby', attributes.id);
      }

      document.head.appendChild(script);
      return script;
    } catch (error) {
      console.error(`Error injecting script ${name}:`, error);
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'script_injection_error',
          script_name: name,
          error_message: error.message
        });
      }
      throw error;
    }
  }

  /**
   * Injects Google Tag Manager script
   * @param {string} tagManagerId - GTM ID
   */
  static injectGoogleTagManager(tagManagerId) {
    if (!tagManagerId) return;
    
    try {
      // Add GTM script
      this.injectScript({
        content: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-${tagManagerId}');`,
        name: 'GTM'
      });

      // Add GTM noscript iframe with accessibility attributes
      const noscript = document.createElement('noscript');
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=GTM-${tagManagerId}`;
      iframe.height = "0";
      iframe.width = "0";
      iframe.style.cssText = "display:none;visibility:hidden";
      iframe.title = "Google Tag Manager";
      iframe.setAttribute('aria-hidden', 'true');
      noscript.appendChild(iframe);
      document.body.appendChild(noscript);
    } catch (error) {
      console.error('Error injecting Google Tag Manager:', error);
      throw error;
    }
  }

  static injectGoogleTagManagerNoscript(tagManagerId) {
    if (!tagManagerId) return;
    
    try {
      const noscript = document.createElement('noscript');
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=GTM-${tagManagerId}`;
      iframe.height = "0";
      iframe.width = "0";
      iframe.style.cssText = "display:none;visibility:hidden";
      iframe.title = "Google Tag Manager";
      iframe.setAttribute('aria-hidden', 'true');
      noscript.appendChild(iframe);
      document.body.appendChild(noscript);
    } catch (error) {
      console.error('Error injecting Google Tag Manager noscript:', error);
      throw error;
    }
  }

  /**
   * Injects Google Analytics script
   * @param {string} analyticsId - GA Measurement ID
   */
  static injectGoogleAnalytics(analyticsId) {
    if (!analyticsId) return;
    
    try {
      this.injectScript({
        src: `https://www.googletagmanager.com/gtag/js?id=${analyticsId}`,
        name: 'Google Analytics Base'
      });
      
      this.injectScript({
        content: `window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${analyticsId}');`,
        name: 'Google Analytics Config'
      });
    } catch (error) {
      console.error('Error injecting Google Analytics:', error);
      throw error;
    }
  }

  /**
   * Injects Hotjar script
   * @param {string} hotjarId - Hotjar Site ID
   */
  static injectHotjar(hotjarId) {
    if (!hotjarId) return;
    
    try {
      this.injectScript({
        content: `
          (function(h,o,t,j,a,r){
            h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
            h._hjSettings={hjid:${hotjarId},hjsv:6};
            a=o.getElementsByTagName('head')[0];
            r=o.createElement('script');r.async=1;
            r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
            a.appendChild(r);
          })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
        name: 'Hotjar'
      });
    } catch (error) {
      console.error('Error injecting Hotjar:', error);
      throw error;
    }
  }

  /**
   * Injects chatbot script based on type
   * @param {ChatbotConfig} config - Chatbot configuration
   */
  static injectChatbot({ type, customId }) {
    if (!type || !customId) return;
    
    try {
      const chatbotConfigs = {
        2: { // Crisp
          content: `window.$crisp=[];window.CRISP_WEBSITE_ID="${customId}";(function(){d=document;s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();`,
          name: 'Crisp Chat'
        },
        3: { // Hubspot
          src: `//js-eu1.hs-scripts.com/${customId}.js`,
          attributes: {
            type: 'text/javascript',
            id: 'hs-script-loader',
            async: true,
            defer: true
          },
          name: 'Hubspot Chat'
        },
        4: { // chatbot.com
          content: `window.__be = window.__be || {};window.__be.id = "${customId}";(function() { var be = document.createElement("script"); be.type = "text/javascript"; be.async = true; be.src = ("https:" == document.location.protocol ? "https://" : "http://") + "cdn.chatbot.com/widget/plugin.js"; var s = document.getElementsByTagName("script")[0]; s.parentNode.insertBefore(be, s);})();`,
          name: 'Chatbot.com'
        },
        5: { // landbot.io
          content: `window.addEventListener("mouseover", initLandbot, { once: true }); window.addEventListener("touchstart", initLandbot, { once: true }); var myLandbot; function initLandbot() {if (!myLandbot) {var s = document.createElement("script");s.type = "text/javascript";s.async = true;s.addEventListener("load", function() {var myLandbot = new Landbot.Livechat({configUrl: "https://storage.googleapis.com/landbot.online/v3/${customId}/index.json",});});s.src = "https://cdn.landbot.io/landbot-3/landbot-3.0.0.js";var x = document.getElementsByTagName("script")[0];x.parentNode.insertBefore(s, x);}}`,
          name: 'Landbot'
        }
      };

      const config = chatbotConfigs[type];
      if (config) {
        this.injectScript(config);
      }
    } catch (error) {
      console.error('Error injecting chatbot:', error);
      throw error;
    }
  }

  /**
   * Updates favicon links
   * @param {string} faviconUrl - URL of the favicon
   */
  static updateFavicon(faviconUrl) {
    if (!faviconUrl) return;

    try {
      const start = performance.now();
      const links = document.querySelectorAll("link[rel~='icon']");
      links.forEach(link => {
        link.href = faviconUrl;
      });

      const duration = performance.now() - start;
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'favicon_update',
          update_time_ms: Math.round(duration)
        });
      }
    } catch (error) {
      console.error('Error updating favicon:', error);
      throw error;
    }
  }
}

// Default export for backward compatibility
export default ScriptInjectionService; 